import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { fetchAssignedLeavesByEmail } from "../../redux/assignLeaveSlice";
import { fetchLeaveLimits } from "../../redux/leaveLimitSlice";
import { autoAssignAllLeaveLimits } from "../../redux/assignLeaveSlice";
import toast from "react-hot-toast";

const ApplyLeaveModal = ({ isModalOpen, setIsModalOpen, onSubmit, isEdit = false, employees = [] }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, reset, setValue, watch } = useForm();
  const [isMultipleDays, setIsMultipleDays] = useState(false);
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { assignedLeaves, status: assignStatus, error: assignError } = useSelector(
    (state) => state.assignLeave
  );
  const { leaveLimits, status: leaveLimitStatus } = useSelector(
    (state) => state.leaveLimits
  );

  const auth = JSON.parse(localStorage.getItem("user"));
  const teacherEmail = auth?.email;

  useEffect(() => {
    if (isModalOpen && teacherEmail) {
      dispatch(fetchAssignedLeavesByEmail(teacherEmail));
      dispatch(fetchLeaveLimits());
    }
  }, [isModalOpen, teacherEmail, dispatch]);

  useEffect(() => {
    if (assignedLeaves.length > 0) {
      setAvailableLeaveTypes(assignedLeaves.map(leave => leave.leaveType));
    }
  }, [assignedLeaves]);

  const handleFormSubmit = async (data) => {
    setIsLoading(true);
    try {
      // Format the data to match backend expectations
      const leaveData = {
        teacherEmail: teacherEmail,
        leaveType: data.leaveType,
        dateFrom: data.dateFrom,
        dateTo: isMultipleDays && data.dateTo ? data.dateTo : null,
        reason: data.reason || "",
      };

      await onSubmit(leaveData);
      reset();
      setIsModalOpen(false);
      toast.success("Leave applied successfully!");
    } catch (error) {
      console.error("Error applying leave:", error);
      toast.error(error.message || "Failed to apply leave");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAutoAssign = async () => {
    try {
      await dispatch(autoAssignAllLeaveLimits(teacherEmail)).unwrap();
      toast.success("All leave types assigned successfully!");
      dispatch(fetchAssignedLeavesByEmail(teacherEmail));
    } catch (error) {
      toast.error(error.message || "Failed to auto-assign leave types");
    }
  };

  const handleClose = () => {
    reset();
    setIsModalOpen(false);
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-[90%] max-w-md">
        <div className="flex justify-between items-center p-4 border-b">
          <h2 className="text-xl font-semibold">
            {isEdit ? "Edit Leave Application" : "Apply for Leave"}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Leave Type */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Leave Type</label>
              {availableLeaveTypes.length > 0 ? (
                <select
                  {...register("leaveType", { required: "Leave type is required" })}
                  className="mt-2 w-full border rounded p-2"
                >
                  <option value="">Select Leave Type</option>
                  {availableLeaveTypes.map((leaveType, index) => (
                    <option key={index} value={leaveType}>
                      {leaveType}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-2">
                  <p className="text-red-500 text-sm mb-2">
                    No leave types assigned. Please contact HR to assign leave types.
                  </p>
                  <button
                    type="button"
                    onClick={handleAutoAssign}
                    className="bg-blue-500 text-white px-3 py-1 rounded text-sm"
                  >
                    Auto-Assign All Leave Types
                  </button>
                </div>
              )}
            </div>

            {/* Date Selection */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Select Days</label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="oneDay"
                    checked={!isMultipleDays}
                    onChange={() => setIsMultipleDays(false)}
                    className="mr-2"
                  />
                  One Day
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="multipleDays"
                    checked={isMultipleDays}
                    onChange={() => setIsMultipleDays(true)}
                    className="mr-2"
                  />
                  Multiple Days
                </label>
              </div>
            </div>

            {/* Date From */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Date From</label>
              <input
                type="date"
                {...register("dateFrom", { required: "Date from is required" })}
                className="mt-2 w-full border rounded p-2"
                min={new Date().toISOString().split("T")[0]}
              />
            </div>

            {/* Date To */}
            {isMultipleDays && (
              <div className="mb-4">
                <label className="block text-gray-700 font-medium">Date To</label>
                <input
                  type="date"
                  {...register("dateTo")}
                  className="mt-2 w-full border rounded p-2"
                  min={watch("dateFrom") || new Date().toISOString().split("T")[0]}
                />
              </div>
            )}

            {/* Reason */}
            <div className="mb-4">
              <label className="block text-gray-700 font-medium">Reason</label>
              <textarea
                {...register("reason")}
                className="mt-2 w-full border rounded p-2"
                rows="3"
                placeholder="Please provide a reason for your leave request..."
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading || availableLeaveTypes.length === 0}
                className="px-4 py-2 bg-[#241763] text-white rounded hover:bg-[#1a0f47] disabled:opacity-50"
              >
                {isLoading ? "Submitting..." : isEdit ? "Update" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplyLeaveModal;